import { useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import { VStack, Box, Image, HStack } from 'native-base';
import { find, flatMap, isEmpty, last, orderBy, partition } from 'lodash';
import moment from 'moment';

import { Button, Text } from '@pimm/base';
import { useSiteConfig, useSiteTime } from '@app/features/store-core';
import { useAuthToken } from '@app/features/app';
import { EmployeeCelebrationList } from './employee-celebration-list';
import { CalendarEvent, useCalendarEvents } from '../context/calendar-events-context';
import { CalendarSnooze, CalendarSnoozeData, SnoozeType } from '../store/calendar.store';
import { useAppLocale } from '@pimm/common';

type EmployeeCelebrationProps = {
  _container?: React.ComponentProps<typeof VStack>;
  autoSave?: boolean;
  onClose?: () => void;
};

export const EmployeeCelebration = ({ _container, autoSave, ..._props }: EmployeeCelebrationProps) => {
  const { translate, locale } = useAppLocale();
  const { user } = useAuthToken();
  const siteTime = useSiteTime();
  const { siteConfig } = useSiteConfig();
  const { getEventsByDate } = useCalendarEvents();
  const [events, setEvents] = useState<CalendarEvent[][]>([]);
  const [currentDayblock] = useState<Date>(siteTime.toDayBlockNow()?.startTime);
  const [snooze, setSnooze] = useState<CalendarSnoozeData>();

  const celebrationModule = useMemo(() => {
    const sections = find(siteConfig?.companyInfo?.brand?.navSections, _ => _.appType?.toLowerCase() === 'dop')?.sections;
    return find(flatMap(sections, 'modules'), _ => _.identifier?.toLowerCase() === 'celebrations');
  }, [siteConfig?.id, locale]);

  const handleChangeSnooze = (snoozeType: SnoozeType) => async () => {
    if (siteConfig?.id && user?.userId) {
      const dayBlocks = siteTime.toDayBlocks();
      const timestamp = snoozeType === SnoozeType.Shift ? siteTime.toDayBlockNow()?.startTime : last(dayBlocks)?.startTime;
      await CalendarSnooze.set(siteConfig?.id, user?.userId, { type: snoozeType, timestamp });
    }
    _props.onClose?.();
  };

  useEffect(() => {
    const eventsToday = getEventsByDate(currentDayblock);
    const rememberSnooze = async (siteId: string, userId: string) => {
      const currentSnooze = await CalendarSnooze.get(siteId, userId);
      setSnooze(currentSnooze);
      if (autoSave) {
        await CalendarSnooze.set(siteId, userId, { type: currentSnooze.type, timestamp: currentDayblock });
      };
    };
    if (siteConfig?.id && user?.userId) rememberSnooze(siteConfig?.id, user?.userId);
    setEvents(partition(orderBy(eventsToday, ['name'], ['asc']), e => e.type === 'Birthday'));
  }, [currentDayblock]);

  return (
    <VStack space={2.5} p={3} {..._container}>
      <Box rounded="xl" w="full" style={{ aspectRatio: 16 / 9 }} overflow="hidden">
        <Image source={{ uri: '/images/celebration.png' }} resizeMode="cover" resizeMethod="auto" w="full" h="full" />
      </Box>

      <HStack space={4} alignItems="center" w="full">
        <VStack w="70px" rounded="lg" borderWidth="1" borderColor="gray.300" alignItems="center" shadow="1">
          <Box bgColor="gray.50" w="full" p={1} alignItems="center" roundedTop="lg">
            <Text fontWeight={600} size="lg" color="gray.700" textAlign="center">
              {moment(currentDayblock).format('MMM').toUpperCase()}
            </Text>
          </Box>
          <Box px={1} py={0.5} w="full">
            <Text fontWeight={800} size="3xl" color="primary.700" textAlign="center">
              {moment(currentDayblock).format('D')}
            </Text>
          </Box>
        </VStack>

        <VStack flex={1}>
          <Text fontWeight={700} size="xl" color="gray.900">
            {translate(celebrationModule?.title, celebrationModule?.titleTranslations)}
          </Text>
          <Text fontWeight={400} size="md" color="gray.600">
            {translate(celebrationModule?.subTitle, celebrationModule?.subTitleTranslations)}
          </Text>
        </VStack>
      </HStack>

      <HStack flex={1} space={3} minH={260}>
        <EmployeeCelebrationList maxW="40%" celebrationType="Birthday" date={currentDayblock} data={events[0]} />
        <EmployeeCelebrationList celebrationType="Anniversary" date={currentDayblock} data={events[1]} />
      </HStack>

      <HStack flex={1} space={2} justifyContent="flex-end">
        {!autoSave ?
          (<Button variant="unstyled" shadow={0} bg="white" borderColor="gray.300" onPress={_props?.onClose}>{`${t('calendar:close')}`}</Button>)
          :
          (<>
            <Button disabled={!autoSave && snooze?.type === SnoozeType.Shift} variant="unstyled" shadow={0} bg="white" borderColor="gray.300" onPress={handleChangeSnooze(SnoozeType.Shift)}>
              {`${t('calendar:remind_later')}`}
            </Button>
            <Button disabled={!autoSave && snooze?.type === SnoozeType.Day} shadow={0} onPress={handleChangeSnooze(SnoozeType.Day)}>{`${t('calendar:dismiss')}`}</Button>
          </>)}
      </HStack>
    </VStack>
  );
};
